import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// Opción de variante (producto + talla) listada para los selectores de los
// modales de venta, pedido y compra.
export interface VariantOption {
  id: string;
  productId: string;
  productName: string;
  sizeId: string;
  sizeName: string;
  sizeRange: string;
  sku: string;
  available: number;
  retailPrice: number;
  unitCost: number;
  // Línea comercial del producto: 'premium' (envío incluido) o 'sencilla' (cobra flete).
  lineType: string;
  // Si es true, el pedido/venta suma el flete de Interrapidísimo.
  chargeShipping: boolean;
  // Peso aproximado (kg) de la talla, usado para el cálculo de flete por peso.
  weightKg: number;
}

export interface VariantsResult {
  data: VariantOption[];
}

// Consulta las variantes reales desnormalizando producto, talla y stock.
export async function fetchVariants(): Promise<VariantsResult> {
  if (!isSupabaseConfigured || !supabase) {
    return { data: [] };
  }

  try {
    const [variantsRes, productsRes, sizesRes, inventoryRes] = await Promise.all([
      supabase.from('product_variants').select('*'),
      supabase.from('products').select('id, name, unit_cost, retail_price, line_type, charge_shipping'),
      supabase.from('sizes').select('id, name, cm_range, weight_kg'),
      supabase.from('inventory').select('variant_id, available'),
    ]);

    if (variantsRes.error) throw new Error(variantsRes.error.message);
    if (productsRes.error) throw new Error(productsRes.error.message);
    if (sizesRes.error) throw new Error(sizesRes.error.message);
    if (inventoryRes.error) throw new Error(inventoryRes.error.message);

    const productMap = new Map(
      ((productsRes.data ?? []) as Array<Record<string, any>>).map((p) => [p.id, p]),
    );
    const sizeMap = new Map(
      ((sizesRes.data ?? []) as Array<Record<string, any>>).map((s) => [s.id, s]),
    );
    const availMap = new Map(
      ((inventoryRes.data ?? []) as Array<Record<string, any>>).map((i) => [
        i.variant_id,
        Number(i.available ?? 0),
      ]),
    );

    const data: VariantOption[] = ((variantsRes.data ?? []) as Array<Record<string, any>>)
      .filter((v) => v.is_active !== false)
      .map((v) => {
        const p = productMap.get(v.product_id);
        const sz = sizeMap.get(v.size_id);
        return {
          id: v.id,
          productId: v.product_id ?? '',
          productName: p?.name ?? '',
          sizeId: v.size_id ?? '',
          sizeName: sz?.name ?? '',
          sizeRange: sz?.cm_range ?? '',
          sku: v.sku ?? '',
          available: availMap.get(v.id) ?? 0,
          retailPrice: Number(p?.retail_price ?? 0),
          unitCost: Number(p?.unit_cost ?? 0),
          lineType: p?.line_type ?? 'premium',
          chargeShipping: Boolean(p?.charge_shipping ?? false),
          weightKg: Number(sz?.weight_kg ?? 0.5),
        };
      },
    );

    return { data };
  } catch (err) {
    console.error('[variants] connection failed:', err);
    throw err;
  }
}